import { FC, useMemo, useState } from 'react';

import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  WEEKDAY_LABELS,
  buildHourStats,
  fmt,
  getNiceScale,
} from './analysisUtils';

interface Props {
  orderList: GiftOrderForAdmin[] | null;
}

const hourLabel = (h: number): string => `${h}시`;

const windowLabel = (start: number): string => `${start}~${(start + 3) % 24}시`;

/** 야간 = 20시~익일 8시 (상담 불가 시간대 구매 비중) */
const isNightHour = (h: number): boolean => h >= 20 || h < 8;

export const PurchaseHourSection: FC<Props> = function PurchaseHourSection({
  orderList,
}) {
  const [hover, setHover] = useState<number | null>(null);

  const stats = useMemo(
    () => (orderList ? buildHourStats(orderList) : null),
    [orderList],
  );

  if (!stats || stats.total === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">결제시각이 기록된 주문이 없습니다.</p>
      </div>
    );
  }

  const { hours, peakHour, peakWindow, heatmap, heatmapMax } = stats;
  const maxCount = Math.max(...hours.map((h) => h.count), 1);
  const countScale = getNiceScale(maxCount);

  // 표에 쓸 누적 비중 (0시부터 순서대로)
  let cum = 0;
  const hourRows = hours.map((h) => {
    cum += h.count;
    return {
      ...h,
      cumRate: stats.total > 0 ? (cum / stats.total) * 100 : 0,
      revenueShare: stats.totalRevenue > 0 ? (h.revenue / stats.totalRevenue) * 100 : 0,
    };
  });

  const nightCount = hours
    .filter((h) => isNightHour(h.hour))
    .reduce((s, h) => s + h.count, 0);
  const nightShare = (nightCount / stats.total) * 100;
  const weekendShare = (stats.weekendCount / stats.total) * 100;

  return (
    <div className="space-y-6">
      {/* 시간대 핵심 지표 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">최다 구매 시간</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {peakHour ? hourLabel(peakHour.hour) : '-'}
          </p>
          {peakHour && (
            <p className="mt-1 text-xs text-gray-400">
              {fmt(peakHour.count)}건 · {peakHour.share.toFixed(1)}%
            </p>
          )}
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">집중 3시간대</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {peakWindow ? windowLabel(peakWindow.startHour) : '-'}
          </p>
          {peakWindow && (
            <p className="mt-1 text-xs text-gray-400">
              전체의 {peakWindow.share.toFixed(1)}% ({fmt(peakWindow.count)}건)
            </p>
          )}
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">야간 구매 비중</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {nightShare.toFixed(1)}%
          </p>
          <p className="mt-1 text-xs text-gray-400">
            20시~익일 8시 · {fmt(nightCount)}건
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">주말 구매 비중</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {weekendShare.toFixed(1)}%
          </p>
          <p className="mt-1 text-xs text-gray-400">
            토·일 · {fmt(stats.weekendCount)}건
          </p>
        </div>
      </div>

      {/* 시간대별 주문 건수 (24시간) */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-1 text-lg font-bold text-gray-800">시간대별 주문 건수</h2>
        <p className="mb-6 text-xs text-gray-400">
          결제시각(KST) 기준 · 총 {fmt(stats.total)}건
          {stats.skippedCount > 0 && ` (결제시각 없는 ${fmt(stats.skippedCount)}건 제외)`}
          {' · 초록 막대 = 최다 시간대'}
        </p>
        <div className="flex min-h-[220px]">
          {/* Y축 눈금 */}
          <div className="relative mr-2 shrink-0" style={{ width: 32 }}>
            {countScale.ticks.map((t) => (
              <span
                key={t}
                className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
                style={{ bottom: `${(t / countScale.niceMax) * 100}%` }}
              >
                {fmt(t)}
              </span>
            ))}
          </div>
          {/* 플롯 영역 */}
          <div className="relative flex-1">
            {countScale.ticks.map((t) => (
              <div
                key={t}
                className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
                style={{ bottom: `${(t / countScale.niceMax) * 100}%` }}
              />
            ))}
            <div className="absolute inset-0 flex items-end gap-[2px]">
              {hours.map((h) => {
                const pct =
                  countScale.niceMax > 0 ? (h.count / countScale.niceMax) * 100 : 0;
                const isPeak = peakHour?.hour === h.hour && h.count > 0;
                return (
                  <div
                    key={h.hour}
                    className="relative flex h-full flex-1 flex-col justify-end"
                    onMouseEnter={() => setHover(h.hour)}
                    onMouseLeave={() => setHover(null)}
                  >
                    {hover === h.hour && (
                      <div
                        className={`pointer-events-none absolute bottom-full z-10 mb-1 rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow ${
                          h.hour >= 20
                            ? 'right-0'
                            : h.hour <= 3
                            ? 'left-0'
                            : 'left-1/2 -translate-x-1/2'
                        }`}
                      >
                        {hourLabel(h.hour)}대 · {fmt(h.count)}건 ({h.share.toFixed(1)}%)
                        <br />
                        매출 {fmt(h.revenue)}원 · 객단가 {fmt(h.avgRevenue)}원
                      </div>
                    )}
                    <div
                      className={`w-full rounded-t transition-all duration-500 ${
                        isPeak ? 'bg-emerald-500' : 'bg-indigo-400'
                      } ${hover === h.hour ? 'opacity-100' : 'opacity-90'}`}
                      style={{
                        height: `${pct}%`,
                        minHeight: h.count > 0 ? 3 : 0,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* X축 라벨 — 3시간 간격만 표기 */}
        <div className="flex">
          <div className="mr-2 shrink-0" style={{ width: 32 }} />
          <div className="flex flex-1 gap-[2px]">
            {hours.map((h) => (
              <span
                key={h.hour}
                className="mt-2 flex-1 text-center text-[10px] leading-none text-gray-500"
              >
                {h.hour}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 요일 × 시간 히트맵 (1시간 단위) */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-1 text-lg font-bold text-gray-800">요일 × 시간 히트맵</h2>
        <p className="mb-4 text-xs text-gray-400">
          1시간 단위 주문 건수 · 색이 진할수록 주문이 많음 (최대 {fmt(heatmapMax)}건)
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-[2px] text-center">
            <thead>
              <tr>
                <th className="w-6" />
                {hours.map((h) => (
                  <th
                    key={h.hour}
                    className="text-[10px] font-medium leading-none text-gray-400"
                  >
                    {h.hour}
                  </th>
                ))}
                <th className="pl-2 text-[10px] font-medium text-gray-400">합계</th>
              </tr>
            </thead>
            <tbody>
              {heatmap.map((row, weekday) => (
                <tr key={WEEKDAY_LABELS[weekday]}>
                  <td
                    className={`text-[11px] font-medium ${
                      weekday === 0 || weekday === 6 ? 'text-rose-400' : 'text-gray-500'
                    }`}
                  >
                    {WEEKDAY_LABELS[weekday]}
                  </td>
                  {row.map((count, hour) => {
                    const intensity = heatmapMax > 0 ? count / heatmapMax : 0;
                    return (
                      <td
                        key={`${weekday}-${hour}`}
                        title={`${WEEKDAY_LABELS[weekday]}요일 ${hour}시대 · ${fmt(
                          count,
                        )}건`}
                        className={`h-7 min-w-[22px] rounded text-[10px] font-medium leading-none ${
                          count === 0
                            ? 'text-gray-300'
                            : intensity > 0.6
                            ? 'text-white'
                            : 'text-gray-700'
                        }`}
                        style={{
                          backgroundColor:
                            count === 0
                              ? '#f8fafc'
                              : `rgba(99, 102, 241, ${0.12 + intensity * 0.88})`,
                        }}
                      >
                        {count > 0 ? count : ''}
                      </td>
                    );
                  })}
                  <td className="pl-2 text-[11px] font-bold text-gray-700">
                    {fmt(stats.weekdayTotals[weekday] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 시간대별 상세 (1시간 단위) */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-bold text-gray-800">시간대별 상세</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500">
                <th className="pb-2 pr-4">시간</th>
                <th className="pb-2 pr-4 text-right">건수</th>
                <th className="pb-2 pr-4">분포</th>
                <th className="pb-2 pr-4 text-right">비중</th>
                <th className="pb-2 pr-4 text-right">누적</th>
                <th className="pb-2 pr-4 text-right">매출</th>
                <th className="pb-2 pr-4 text-right">매출비중</th>
                <th className="pb-2 pr-4 text-right">객단가</th>
                <th className="pb-2 text-right">수량</th>
              </tr>
            </thead>
            <tbody>
              {hourRows.map((h) => {
                const isPeak = peakHour?.hour === h.hour && h.count > 0;
                return (
                  <tr key={h.hour} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-4 font-medium whitespace-nowrap text-gray-700">
                      {h.hour}시 ~ {(h.hour + 1) % 24}시
                    </td>
                    <td className="py-1.5 pr-4 text-right font-bold text-gray-900">
                      {fmt(h.count)}
                    </td>
                    <td className="w-40 py-1.5 pr-4">
                      <div className="h-2.5 w-full rounded bg-gray-50">
                        {h.count > 0 && (
                          <div
                            className={`h-full rounded ${
                              isPeak ? 'bg-emerald-500' : 'bg-indigo-400'
                            }`}
                            style={{
                              width: `${Math.max((h.count / maxCount) * 100, 2)}%`,
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 pr-4 text-right text-gray-600">
                      {h.share.toFixed(1)}%
                    </td>
                    <td className="py-1.5 pr-4 text-right font-medium text-indigo-600">
                      {h.cumRate.toFixed(1)}%
                    </td>
                    <td className="py-1.5 pr-4 text-right text-green-600">
                      {fmt(h.revenue)}원
                    </td>
                    <td className="py-1.5 pr-4 text-right text-green-600">
                      {h.revenueShare.toFixed(1)}%
                    </td>
                    <td className="py-1.5 pr-4 text-right font-bold text-indigo-600">
                      {h.avgRevenue > 0 ? `${fmt(h.avgRevenue)}원` : '-'}
                    </td>
                    <td className="py-1.5 text-right text-gray-600">
                      {fmt(h.quantity)}개
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="py-2 pr-4 text-gray-700">합계</td>
                <td className="py-2 pr-4 text-right text-gray-900">{fmt(stats.total)}</td>
                <td className="py-2 pr-4" />
                <td className="py-2 pr-4 text-right text-gray-600">100%</td>
                <td className="py-2 pr-4 text-right text-indigo-600">-</td>
                <td className="py-2 pr-4 text-right text-green-600">
                  {fmt(stats.totalRevenue)}원
                </td>
                <td className="py-2 pr-4 text-right text-green-600">100%</td>
                <td className="py-2 pr-4 text-right text-indigo-600">
                  {fmt(Math.round(stats.totalRevenue / stats.total))}원
                </td>
                <td className="py-2 text-right text-gray-600">
                  {fmt(hourRows.reduce((s, h) => s + h.quantity, 0))}개
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
